import { Tooltip } from "react-bootstrap";
import { CategoryColor, IEvent } from "../core/type";
import { Clock, MapPinPen } from "lucide-react";
import { CustomDateFormate } from "../../../../../utils/date";
import { getEventDates, normalizeDate } from "../core/_utility";

export const getFormattedDate = (date: Date): string => {
    return CustomDateFormate(date);
};

export const getFormattedTime = (date: Date): string => {
    return date.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit'
    });
};

export const formatDateTime = (date: Date | string): string => {
    const d = new Date(date);
    const pad = (n: number): string => n.toString().padStart(2, '0');
    return (
        d.getFullYear() + '-' +
        pad(d.getMonth() + 1) + '-' +
        pad(d.getDate()) + ' ' +
        pad(d.getHours()) + ':' +
        pad(d.getMinutes()) + ':' +
        pad(d.getSeconds())
    );
};

export const getEventStartDateTime = (event: IEvent): Date => {
    if (event.is_all_day) {
        return new Date(event.start_date);
    }
    const dateTimeStr = `${event.start_date}T${event.start_time || '00:00'}`;
    return new Date(dateTimeStr);
}

export const getEventCategoryColor = (
    category: string,
    categoryAndColorsData: CategoryColor[]
): string => {
    return (
        categoryAndColorsData?.find(
            (item: CategoryColor) => item.name === category
        )?.color || ''
    );
};

export const getEventEndDateTime = (event: IEvent): Date => {
    if (event.is_all_day) {
        const endDate = new Date(event.end_date);
        endDate.setHours(23, 59, 59, 999);
        return endDate;
    }
    const dateTimeStr = `${event.end_date}T${event.end_time || '23:59'}`;
    return new Date(dateTimeStr);
}


export const isMultiDayEvent = (event: IEvent): boolean => {
    const { start, end } = getEventDates(event);

    if (start && end) {
        const startDate = normalizeDate(start);
        const endDate = normalizeDate(end);
        return startDate.getTime() !== endDate.getTime();
    }

    // Fallback for backward compatibility
    if (event.start_date && event.end_date) {
        const startDate = normalizeDate(event.start_date);
        const endDate = normalizeDate(event.end_date);
        return startDate.getTime() !== endDate.getTime();
    }

    return false;
};

export const renderTooltip = (event: IEvent, eventCategoryAndColors: CategoryColor[]) => {
    const getFormattedDate = (date: Date): string => {
        return CustomDateFormate(date);
    };

    const getFormattedTime = (date: Date): string => {
        return date.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    let isMultiDay = false;
    let startDateTime: Date;
    let endDateTime: Date;
    let displayText = '';

    const { start, end } = getEventDates(event);

    if (start && end) {
        startDateTime = getEventStartDateTime(event);
        endDateTime = getEventEndDateTime(event);
        isMultiDay = isMultiDayEvent(event);

        if (event.is_all_day) {
            if (isMultiDay) {
                const startDate = getFormattedDate(startDateTime);
                const endDate = getFormattedDate(endDateTime);
                displayText = `${startDate} - ${endDate} (All Day)`;
            } else {
                const date = getFormattedDate(startDateTime);
                displayText = `${date} (All Day)`;
            }
        } else {
            if (isMultiDay) {
                const startDate = getFormattedDate(startDateTime);
                const endDate = getFormattedDate(endDateTime);
                const startTime = getFormattedTime(startDateTime);
                const endTime = getFormattedTime(endDateTime);
                displayText = `${startDate} ${startTime} - ${endDate} ${endTime}`;
            } else {
                const date = getFormattedDate(startDateTime);
                const startTime = getFormattedTime(startDateTime);
                const endTime = getFormattedTime(endDateTime);
                displayText = `${date} • ${startTime} - ${endTime}`;
            }
        }
    } else {
        // Fallback for backward compatibility
        const startDate = event.start_date ? new Date(event.start_date) : new Date();
        const endDate = event.end_date ? new Date(event.end_date) : new Date();
        const startTime = event.start_time ? new Date(`2000-01-01T${event.start_time}`) : new Date();
        const endTime = event.end_time ? new Date(`2000-01-01T${event.end_time}`) : new Date();

        startDateTime = startDate;
        endDateTime = endDate;
        isMultiDay = startDate.toDateString() !== endDate.toDateString();

        if (event.is_all_day) {
            if (isMultiDay) {
                displayText = `${getFormattedDate(startDate)} - ${getFormattedDate(endDate)} (All Day)`;
            } else {
                displayText = `${getFormattedDate(startDate)} (All Day)`;
            }
        } else {
            if (isMultiDay) {
                displayText = `${getFormattedDate(startDate)} ${getFormattedTime(startTime)} - ${getFormattedDate(endDate)} ${getFormattedTime(endTime)}`;
            } else {
                displayText = `${getFormattedDate(startDate)} • ${getFormattedTime(startTime)} - ${getFormattedTime(endTime)}`;
            }
        }
    }

    return (
        <Tooltip id={`tooltip-${event.id}`} className="custom-tooltip">
            <div className="bg-white py-3 px-4 rounded-4 shadow-sm border border-gray-200 text-left mw-100 text-break">
                <div className="fw-semibold fs-8 mb-2 text-gray-700">{event.title}</div>

                <div className="fs-9 text-gray-600 m-0 mb-1 d-flex align-items-center gap-2">
                    <Clock size={12} />
                    {displayText}
                </div>

                {event.location && (
                    <div className="fs-9 text-gray-600 m-0 mb-1 d-flex align-items-center gap-1">
                        <MapPinPen size={12} />
                        {event.location}
                    </div>
                )}
                {event.description && (
                    <div className="fs-9 text-gray-600 m-0 mb-1 text-break">{event.description}</div>
                )}
                <div
                    className="fw-semibold fs-9 px-2 py-1 rounded-4 mt-2 d-inline-block"
                    style={{ backgroundColor: `${getEventCategoryColor(event.category, eventCategoryAndColors)}20`, color: getEventCategoryColor(event.category, eventCategoryAndColors) }}
                >
                    {event.category}
                </div>
            </div>
        </Tooltip>
    );
};